import { defineStore } from 'pinia'
import {
  getSkinClashCurrent,
  getSkinClashHistory,
  postSkinClashJoin,
} from '@/services/api'
import type {
  SkinClashEntry,
  SkinClashRound,
  SkinClashConfig,
} from '@/services/models/skinClash'
import { SKIN_CLASH_DEFAULT_CONFIG } from '@/services/models/skinClash'
import { useProfileStore } from '@/store/profile'

const SKIN_CLASH_HISTORY_LIMIT = 20

interface SkinClashState {
  currentRound: SkinClashRound | null
  recentRounds: SkinClashRound[]
  selectedInventoryItemIds: string[]
  currentLoading: boolean
  historyLoading: boolean
  joinLoading: boolean
  currentError: string | null
  historyError: string | null
  joinError: string | null
  lastEntry: SkinClashEntry | null
}

export const useSkinClashStore = defineStore('skinClash', {
  state: (): SkinClashState => ({
    currentRound: null,
    recentRounds: [],
    selectedInventoryItemIds: [],
    currentLoading: false,
    historyLoading: false,
    joinLoading: false,
    currentError: null,
    historyError: null,
    joinError: null,
    lastEntry: null,
  }),

  getters: {
    config: (state): SkinClashConfig => ({
      ...SKIN_CLASH_DEFAULT_CONFIG,
      ...(state.currentRound?.config ?? {}),
    }),
    selectedCount: (state) => state.selectedInventoryItemIds.length,
  },

  actions: {
    async fetchCurrent() {
      this.currentLoading = true
      this.currentError = null

      try {
        const response = await getSkinClashCurrent()
        if (!response.success) {
          throw new Error(response.error || 'Skin Clash current request failed')
        }
        this.currentRound = response.data?.round ?? null
      } catch (error) {
        this.currentRound = null
        this.currentError = extractApiError(error, 'Skin Clash current request failed')
        console.error('Skin Clash: failed to fetch current round', error)
      } finally {
        this.currentLoading = false
      }
    },

    async fetchHistory() {
      this.historyLoading = true
      this.historyError = null

      try {
        const response = await getSkinClashHistory()
        if (!response.success) {
          throw new Error(response.error || 'Skin Clash history request failed')
        }
        this.recentRounds = normalizeRecentRounds(response.data?.rounds ?? [])
      } catch (error) {
        this.recentRounds = []
        this.historyError = extractApiError(error, 'Skin Clash history request failed')
        console.error('Skin Clash: failed to fetch history', error)
      } finally {
        this.historyLoading = false
      }
    },

    async joinRound(inventoryItemIds = this.selectedInventoryItemIds) {
      this.joinLoading = true
      this.joinError = null

      try {
        const response = await postSkinClashJoin({ inventoryItemIds })
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Skin Clash join request failed')
        }

        this.currentRound = response.data.round
        this.lastEntry = response.data.entry
        this.selectedInventoryItemIds = []

        const profileStore = useProfileStore()
        if (profileStore.inventory) {
          const reserved = new Set(inventoryItemIds)
          profileStore.setInventory(profileStore.inventory.filter((item) => !reserved.has(item.id ?? '')))
        }
        profileStore.fetchInventory()
        profileStore.fetchProfile()

        return response.data
      } catch (error) {
        this.joinError = extractApiError(error, 'Skin Clash join request failed')
        console.error('Skin Clash: failed to join round', error)
        throw error
      } finally {
        this.joinLoading = false
      }
    },

    applyRoundUpdate(round: SkinClashRound) {
      this.currentRound = round
      if (round.status >= 30) {
        this.recentRounds = normalizeRecentRounds([
          round,
          ...this.recentRounds.filter((entry) => entry.id !== round.id),
        ])
      }
    },

    clearCurrentRound() {
      this.currentRound = null
      this.currentError = null
    },

    toggleInventoryItem(itemId: string) {
      const index = this.selectedInventoryItemIds.indexOf(itemId)
      if (index >= 0) {
        this.selectedInventoryItemIds.splice(index, 1)
        return
      }
      this.selectedInventoryItemIds.push(itemId)
    },

    clearSelection() {
      this.selectedInventoryItemIds = []
      this.joinError = null
    },
  },
})

function normalizeRecentRounds(rounds: SkinClashRound[]) {
  return [...rounds].sort(compareRoundsByRecency).slice(0, SKIN_CLASH_HISTORY_LIMIT)
}

function compareRoundsByRecency(a: SkinClashRound, b: SkinClashRound) {
  const byTime = getRoundTimestamp(b) - getRoundTimestamp(a)
  if (byTime !== 0) return byTime

  return b.roundNumber - a.roundNumber
}

function getRoundTimestamp(round: SkinClashRound) {
  const value = round.completedAt ?? round.endsAt ?? round.startsAt
  return value ? new Date(value).getTime() : 0
}

function extractApiError(error: unknown, fallback: string) {
  const apiError = error as {
    body?: { error?: string; message?: string }
    response?: { data?: { error?: string; message?: string } }
    message?: string
  }

  return (
    apiError?.body?.error ||
    apiError?.body?.message ||
    apiError?.response?.data?.error ||
    apiError?.response?.data?.message ||
    apiError?.message ||
    fallback
  )
}
